/**
 * MassnahmeDetailDialog - Detail/Bearbeiten-Dialog fuer Massnahmen
 * (Einnahmen, Ausgaben, Bankkonten, Versicherungen etc.)
 */

import { useState } from 'react';
import {
  X, Save, Trash2, Edit, Euro, FileText, Calendar,
  TrendingUp, TrendingDown, CreditCard, Shield, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const TYP_LABELS = {
  alg1: 'ALG I',
  alg2: 'Buergergeld / ALG II',
  grundsicherung: 'Grundsicherung',
  rente: 'Rente',
  pflegegeld: 'Pflegegeld',
  wohngeld: 'Wohngeld',
  kindergeld: 'Kindergeld',
  krankenversicherung: 'Krankenversicherung',
  pflegeversicherung: 'Pflegeversicherung',
  haftpflicht: 'Haftpflicht',
  girokonto: 'Girokonto',
  sparkonto: 'Sparkonto',
  miete: 'Miete',
  strom: 'Strom',
  telefon: 'Telefon',
  glaeubigerverbindung: 'Glaeubigerverbindung',
  pflegedienst: 'Pflegedienst',
  ambulante_begleitung: 'Ambulante Begleitung',
  abw: 'Ambulant Betreutes Wohnen',
  betreutes_wohnen: 'Betreutes Wohnen',
  tagesstaette: 'Tagesstaette',
  werkstatt: 'Werkstatt',
};

const TYP_ICONS = {
  alg1: TrendingUp,
  alg2: TrendingUp,
  grundsicherung: TrendingUp,
  rente: TrendingUp,
  pflegegeld: TrendingUp,
  wohngeld: TrendingUp,
  kindergeld: TrendingUp,
  krankenversicherung: Shield,
  pflegeversicherung: Shield,
  haftpflicht: Shield,
  girokonto: CreditCard,
  sparkonto: CreditCard,
  miete: TrendingDown,
  strom: TrendingDown,
  telefon: TrendingDown,
  glaeubigerverbindung: FileText,
};

export default function MassnahmeDetailDialog({ data, klientId, onClose, onSaved }) {
  const isNew = !data.id;
  const [editing, setEditing] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    bezeichnung: data.bezeichnung || '',
    typ: data.typ || '',
    betrag: data.betrag || 0,
    kontostand: data.kontostand || 0,
    schuldenstand: data.schuldenstand || 0,
    iban: data.iban || '',
    aktenzeichen: data.aktenzeichen || '',
    status: data.status || 'aktiv',
    notizen: data.notizen || '',
    leistungsgeber: data.leistungsgeber || '',
    turnus: data.turnus || 'monatlich',
  });

  const typ = data.typ || '';
  const isBankkonto = ['girokonto', 'sparkonto'].includes(typ);
  const isGlaeubiger = typ === 'glaeubigerverbindung';
  const IconComp = TYP_ICONS[typ] || Euro;
  const typLabel = TYP_LABELS[typ] || typ || 'Massnahme';

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...formData };
      // Sicherstellen dass Zahlen als Zahlen gesendet werden
      payload.betrag = parseFloat(payload.betrag) || 0;
      payload.kontostand = parseFloat(payload.kontostand) || 0;
      payload.schuldenstand = parseFloat(payload.schuldenstand) || 0;

      if (isNew) {
        // Neue Massnahme erstellen
        payload.person_id = klientId;
        payload.klient_id = klientId;
        const res = await fetch(`${API}/api/betreuung/massnahmen`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Fehler beim Erstellen');
        toast.success('Massnahme erstellt');
      } else {
        // Bestehende Massnahme aktualisieren
        const res = await fetch(`${API}/api/betreuung/massnahmen/${data.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Fehler beim Speichern');
        toast.success('Massnahme aktualisiert');
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!data.id) return;
    if (!window.confirm('Diese Massnahme wirklich loeschen?')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/betreuung/massnahmen/${data.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Fehler beim Loeschen');
      toast.success('Massnahme geloescht');
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Fehler');
    } finally {
      setDeleting(false);
    }
  };

  const betragValue = isBankkonto ? formData.kontostand : isGlaeubiger ? formData.schuldenstand : formData.betrag;
  const betragLabel = isBankkonto ? 'Kontostand' : isGlaeubiger ? 'Schuldenstand' : 'Betrag';
  const betragKey = isBankkonto ? 'kontostand' : isGlaeubiger ? 'schuldenstand' : 'betrag';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="massnahme-detail-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <IconComp className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {isNew ? 'Neue Massnahme' : (data.bezeichnung || typLabel)}
              </h2>
              <p className="text-sm text-slate-500">{typLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!isNew && !editing && (
              <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="massnahme-edit-btn">
                <Edit className="w-4 h-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {editing ? (
            <>
              <div>
                <Label>Bezeichnung</Label>
                <Input
                  value={formData.bezeichnung}
                  onChange={e => setFormData(p => ({ ...p, bezeichnung: e.target.value }))}
                  placeholder="z.B. Grundsicherung SGB XII"
                  data-testid="massnahme-bezeichnung"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{betragLabel} (EUR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={betragValue}
                    onChange={e => setFormData(p => ({ ...p, [betragKey]: e.target.value }))}
                    data-testid="massnahme-betrag"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={v => setFormData(p => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="massnahme-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aktiv">Aktiv</SelectItem>
                      <SelectItem value="ruhend">Ruhend</SelectItem>
                      <SelectItem value="beendet">Beendet</SelectItem>
                      <SelectItem value="beantragt">Beantragt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isBankkonto && (
                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={formData.iban}
                    onChange={e => setFormData(p => ({ ...p, iban: e.target.value }))}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="font-mono"
                    data-testid="massnahme-iban"
                  />
                </div>
              )}
              <div>
                <Label>Aktenzeichen / Referenz</Label>
                <Input
                  value={formData.aktenzeichen}
                  onChange={e => setFormData(p => ({ ...p, aktenzeichen: e.target.value }))}
                  placeholder="z.B. 123/456/789"
                  data-testid="massnahme-aktenzeichen"
                />
              </div>
              <div>
                <Label>Leistungsgeber / Institution</Label>
                <Input
                  value={formData.leistungsgeber}
                  onChange={e => setFormData(p => ({ ...p, leistungsgeber: e.target.value }))}
                  placeholder="z.B. Sozialamt Musterstadt"
                  data-testid="massnahme-leistungsgeber"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Turnus</Label>
                  <Select value={formData.turnus} onValueChange={v => setFormData(p => ({ ...p, turnus: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monatlich">Monatlich</SelectItem>
                      <SelectItem value="quartalsweise">Quartalsweise</SelectItem>
                      <SelectItem value="jaehrlich">Jaehrlich</SelectItem>
                      <SelectItem value="einmalig">Einmalig</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Notizen</Label>
                <Textarea
                  rows={3}
                  value={formData.notizen}
                  onChange={e => setFormData(p => ({ ...p, notizen: e.target.value }))}
                  placeholder="Weitere Informationen..."
                  data-testid="massnahme-notizen"
                />
              </div>
            </>
          ) : (
            /* Read-only view */
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-500">{betragLabel}</span>
                <span className="text-lg font-bold text-slate-800">{Number(betragValue || 0).toLocaleString('de-DE')} EUR</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <Badge className={
                  data.status === 'aktiv' ? 'bg-green-100 text-green-700' :
                  data.status === 'beantragt' ? 'bg-amber-100 text-amber-700' :
                  data.status === 'beendet' ? 'bg-slate-100 text-slate-600' :
                  'bg-blue-100 text-blue-700'
                }>
                  {data.status || 'aktiv'}
                </Badge>
              </div>
              {data.iban && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">IBAN</span>
                  <span className="text-sm font-mono text-slate-700">{data.iban}</span>
                </div>
              )}
              {data.aktenzeichen && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Aktenzeichen</span>
                  <span className="text-sm text-slate-700">{data.aktenzeichen}</span>
                </div>
              )}
              {data.leistungsgeber && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Leistungsgeber</span>
                  <span className="text-sm text-slate-700">{data.leistungsgeber}</span>
                </div>
              )}
              {data.turnus && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Turnus</span>
                  <span className="text-sm text-slate-700 capitalize">{data.turnus}</span>
                </div>
              )}
              {data.notizen && (
                <div>
                  <span className="text-sm text-slate-500">Notizen</span>
                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{data.notizen}</p>
                </div>
              )}
              {data.created_date && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Erstellt am</span>
                  <span className="text-sm text-slate-400">{new Date(data.created_date).toLocaleDateString('de-DE')}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between shrink-0">
          <div>
            {!isNew && (
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                onClick={handleDelete} disabled={deleting}
                data-testid="massnahme-delete-btn"
              >
                {deleting ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Loeschen
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                {!isNew && <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Abbrechen</Button>}
                <Button size="sm" onClick={handleSave} disabled={saving} data-testid="massnahme-save-btn">
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Speichern
                </Button>
              </>
            ) : (
              <Button variant="outline" size="sm" onClick={onClose}>Schliessen</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
